import { useMemo, useState, type FC } from 'react';

import { Button, DatePicker, Drawer, InputNumber, Select } from 'antd';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';
import MemberAvatar from '@src/components/MemberList/MemberAvatar';
import StepIndicator from '@src/components/StepIndicator';
import { ResponsiveTable } from '@src/components/Table';
import type { ResponsiveTableColumns } from '@src/components/Table';
import { ALLOWED_ROLES } from '@src/packages/utils/access';
import {
  formatNationalId,
  getOrganizationCountry,
  type OrganizationCountrySource,
} from '@src/packages/utils/nationalId';
import { resolveUserDisplayName } from '@src/packages/utils/user';
import { JOURNEY_ASSIGNED_USERS_TYPE } from '@src/packages/workJourney/constants';
import IntlMessages from '@src/util/IntlMessages';

import UsersSelectionDrawer from '../../../WorkJourneyCreate/components/UsersSelectionDrawer';
import { PlanMonthlyJourneyAddUsersDrawer } from './components/PlanMonthlyJourneyAddUsersDrawer';
import { PlanMonthlyJourneySuccessModal } from './components/PlanMonthlyJourneySuccessModal';
import { PlanMonthlyJourneyUnderMinModal } from './components/PlanMonthlyJourneyUnderMinModal';
import { usePlanMonthlyJourney } from './hooks/usePlanMonthlyJourney';
import styles from './styles.module.less';
import type { PlanMonthlyJourneyDrawerProps, PlanMonthlyJourneyMember } from './types';
import { disablePastPlanDates, formatPlanDateDisplay } from './utils/resolvePlanPeriod';
import { dedupeById } from './utils/sanitizePlanPayload';
import ActionButton from '@src/components/Buttons/ActionButton';

const CoverStepIndicator = StepIndicator as FC<{
  currentStep: number;
  totalSteps: number;
  stepCounterId?: string;
}>;

export const PlanMonthlyJourneyDrawer = ({
  open,
  onClose,
  initialWorkJourneyConfigId,
  onSaved,
}: PlanMonthlyJourneyDrawerProps) => {
  const intl = useIntl();
  const organizationCountry = useSelector(
    ({
      organizations,
    }: {
      organizations: { organization?: OrganizationCountrySource };
    }) => getOrganizationCountry(organizations.organization),
  );
  const [supervisorsDrawerOpen, setSupervisorsDrawerOpen] = useState(false);

  const plan = usePlanMonthlyJourney({
    open,
    initialWorkJourneyConfigId,
    onClose,
    onSaved,
  });

  const title =
    plan.step === 1
      ? intl.formatMessage({ id: 'workJourney.management.plan.title' })
      : intl.formatMessage(
          { id: 'workJourney.management.plan.titleWithJourney' },
          { journey: plan.selectedConfig?.name || '—' },
        );

  const primaryAction =
    plan.step === 3
      ? {
          label: intl.formatMessage({ id: 'general.save' }),
          type: 'primary' as const,
          action: plan.handleSave,
          loading: plan.saving,
          disabled: plan.saving,
          allowedRole: ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE,
        }
      : {
          label: intl.formatMessage({ id: 'button.continue' }),
          type: 'primary' as const,
          action: plan.goNext,
          allowedRole: ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE,
        };

  const memberColumns: ResponsiveTableColumns<PlanMonthlyJourneyMember> = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'workJourney.management.plan.columns.user' }),
        key: 'user',
        render: (record: PlanMonthlyJourneyMember) => (
          <div className={styles.userCell}>
            <MemberAvatar
              size={32}
              user={{ displayName: record.displayName, photoURL: record.photoURL ?? undefined }}
            />
            <span>{record.displayName}</span>
          </div>
        ),
      },
      {
        title: intl.formatMessage({ id: 'workJourney.management.plan.columns.idCard' }),
        key: 'idCard',
        render: (record: PlanMonthlyJourneyMember) =>
          formatNationalId(organizationCountry, record.idCard || '') || '—',
      },
      {
        title: intl.formatMessage({ id: 'workJourney.management.plan.columns.job' }),
        key: 'job',
        render: (record: PlanMonthlyJourneyMember) => record.job?.name || '—',
      },
      ...(plan.step === 2
        ? [
            {
              title: intl.formatMessage({ id: 'workJourney.management.plan.columns.journey' }),
              key: 'journey',
              render: (record: PlanMonthlyJourneyMember) =>
                record.previousWorkJourney?.workJourneyConfigName ||
                intl.formatMessage({ id: 'workJourney.management.plan.noJourney' }),
            },
            {
              title: '',
              key: 'remove',
              width: 56,
              render: (record: PlanMonthlyJourneyMember) => (
                <ActionButton.Container
                  buttons={[
                    <ActionButton
                      icon="delete"
                      tooltipText={intl.formatMessage({ id: 'addresses.actions.delete' })}
                      allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.ADDRESSES.DELETE}
                      color="danger"
                      onClick={() => plan.removeMember(record.id)}
                    />,
                  ]}
                />
              ),
            },
          ]
        : []),
    ],
    [intl, organizationCountry, plan],
  );

  const counterClass =
    plan.form.members.length >= plan.form.requiredMembers ? styles.counterOk : styles.counterLow;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        width={720}
        destroyOnClose
        title={null}
        closable={false}
        footer={null}
        className={styles.drawer}
      >
        <BoxContainer
          loading={
            plan.shiftsLoading || plan.addressesLoading || plan.configsLoading || plan.saving
          }
        >
          <BoxContainer content fixed bottomBorder>
            <FilterContainer
              title={<Title.Header value={title} />}
              goBack={plan.step === 1 ? onClose : plan.goBack}
              actionButtons={[primaryAction]}
            />
            <CoverStepIndicator
              currentStep={plan.step}
              totalSteps={3}
              stepCounterId="workJourney.management.plan.stepCounter"
            />
          </BoxContainer>

          <BoxContainer content>
            {plan.step === 1 && (
              <>
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <IntlMessages id="workJourney.management.plan.section.selectJourney" />
                  </h3>
                  <div className={styles.field}>
                    <label className={styles.label}>
                      <IntlMessages id="workJourney.management.plan.fields.shift" />
                      <span className={styles.required}>*</span>
                    </label>
                    <Select
                      className={styles.fullWidth}
                      value={plan.form.workShiftId ?? undefined}
                      onChange={plan.handleShiftChange}
                      options={plan.shiftOptions.map(option => ({
                        value: option.id,
                        label: option.label,
                      }))}
                      placeholder={intl.formatMessage({
                        id: 'workJourney.management.plan.fields.shiftPlaceholder',
                      })}
                      showSearch
                      optionFilterProp="label"
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label}>
                      <IntlMessages id="workJourney.management.plan.fields.journey" />
                      <span className={styles.required}>*</span>
                    </label>
                    <Select
                      className={styles.fullWidth}
                      value={plan.form.workJourneyConfigId ?? undefined}
                      onChange={plan.handleJourneyChange}
                      options={plan.journeyOptions.map(option => ({
                        value: option.id,
                        label: option.name,
                      }))}
                      placeholder={intl.formatMessage({
                        id: 'workJourney.management.plan.fields.journeyPlaceholder',
                      })}
                      showSearch
                      optionFilterProp="label"
                      disabled={!plan.form.workShiftId}
                    />
                  </div>
                </section>

                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <IntlMessages id="workJourney.management.plan.section.period" />
                  </h3>
                  <div className={styles.field}>
                    <label className={styles.label}>
                      <IntlMessages id="workJourney.management.plan.fields.startDate" />
                      <span className={styles.required}>*</span>
                    </label>
                    <DatePicker
                      className={styles.fullWidth}
                      value={plan.form.startDate}
                      onChange={plan.setStartDate}
                      format="DD/MM/YY"
                      disabledDate={disablePastPlanDates}
                    />
                  </div>
                </section>

                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <IntlMessages id="workJourney.management.plan.section.location" />
                  </h3>
                  <div className={styles.field}>
                    <label className={styles.label}>
                      <IntlMessages id="workJourney.management.plan.fields.location" />
                      <span className={styles.required}>*</span>
                    </label>
                    <Select
                      className={styles.fullWidth}
                      value={plan.form.addressId ?? undefined}
                      onChange={plan.setAddressId}
                      options={plan.addressOptions.map(option => ({
                        value: option.id,
                        label: option.label,
                      }))}
                      placeholder={intl.formatMessage({
                        id: 'workJourney.management.plan.fields.locationPlaceholder',
                      })}
                      showSearch
                      optionFilterProp="label"
                    />
                  </div>
                </section>

                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <IntlMessages id="workJourney.management.plan.section.supervisors" />
                  </h3>
                  <div className={styles.field}>
                    <label className={styles.label}>
                      <IntlMessages id="workJourney.management.plan.fields.supervisors" />
                      <span className={styles.required}>*</span>
                    </label>
                    <Select
                      className={styles.fullWidth}
                      mode="multiple"
                      open={false}
                      value={plan.form.supervisors.map(supervisor => supervisor.id)}
                      options={plan.form.supervisors.map(supervisor => ({
                        value: supervisor.id,
                        label: `${supervisor.displayName}${
                          supervisor.idCard
                            ? ` (${formatNationalId(organizationCountry, supervisor.idCard)})`
                            : ''
                        }`,
                      }))}
                      onClick={() => setSupervisorsDrawerOpen(true)}
                      onDeselect={userId =>
                        plan.setSupervisors(
                          plan.form.supervisors.filter(supervisor => supervisor.id !== userId),
                        )
                      }
                      placeholder={intl.formatMessage({
                        id: 'workJourney.management.plan.fields.supervisorsPlaceholder',
                      })}
                    />
                  </div>
                </section>
              </>
            )}

            {plan.step === 2 && (
              <>
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <IntlMessages id="workJourney.management.plan.section.selectJourney" />
                  </h3>
                  <div className={styles.field}>
                    <label className={styles.label}>
                      <IntlMessages id="workJourney.management.plan.fields.requiredMembers" />
                    </label>
                    <InputNumber
                      className={styles.fullWidth}
                      min={1}
                      value={plan.form.requiredMembers}
                      onChange={value => plan.setRequiredMembers(Number(value) || 1)}
                    />
                  </div>
                </section>

                <section className={styles.section}>
                  <div className={styles.step2Header}>
                    <h3 className={styles.sectionTitle} style={{ margin: 0 }}>
                      <IntlMessages id="workJourney.management.plan.section.members" />
                    </h3>
                    <Button type="primary" onClick={() => plan.setAddUsersOpen(true)}>
                      <IntlMessages id="workJourney.management.plan.addUsers.button" />
                    </Button>
                  </div>
                  <div className={styles.filtersRow}>
                    <span className={`${styles.counter} ${counterClass}`}>
                      {plan.form.members.length}/{plan.form.requiredMembers}
                    </span>
                  </div>
                  <ResponsiveTable columns={memberColumns} dataSource={plan.form.members} />
                </section>
              </>
            )}

            {plan.step === 3 && (
              <>
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <IntlMessages id="workJourney.management.plan.section.summary" />
                  </h3>
                  <div className={styles.summaryGrid}>
                    <span className={styles.summaryLabel}>
                      <IntlMessages id="workJourney.management.plan.fields.startDate" />
                    </span>
                    <span className={styles.summaryValue}>
                      {formatPlanDateDisplay(plan.planStartIso)}
                    </span>
                    <span className={styles.summaryLabel}>
                      <IntlMessages id="workJourney.management.plan.fields.journey" />
                    </span>
                    <span className={styles.summaryValue}>{plan.selectedConfig?.name || '—'}</span>
                    <span className={styles.summaryLabel}>
                      <IntlMessages id="workJourney.management.plan.fields.shift" />
                    </span>
                    <span className={styles.summaryValue}>{plan.selectedShiftLabel}</span>
                    <span className={styles.summaryLabel}>
                      <IntlMessages id="workJourney.management.plan.fields.location" />
                    </span>
                    <span className={styles.summaryValue}>{plan.selectedAddressLabel}</span>
                    <span className={styles.summaryLabel}>
                      <IntlMessages id="workJourney.management.plan.fields.requiredMembers" />
                    </span>
                    <span className={styles.summaryValue}>{plan.form.requiredMembers}</span>
                  </div>
                </section>

                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <IntlMessages id="workJourney.management.plan.section.members" />
                  </h3>
                  <ResponsiveTable columns={memberColumns} dataSource={plan.form.members} />
                </section>
              </>
            )}
          </BoxContainer>
        </BoxContainer>
      </Drawer>

      <UsersSelectionDrawer
        isVisible={supervisorsDrawerOpen}
        onCancel={() => setSupervisorsDrawerOpen(false)}
        onConfirm={users => {
          plan.setSupervisors(
            dedupeById(
              (
                users as Array<{
                  user?: {
                    id?: string;
                    displayName?: string;
                    firstName?: string;
                    lastName?: string;
                    email?: string;
                    idCard?: string;
                    photoURL?: string;
                  };
                  id?: string;
                  displayName?: string;
                  firstName?: string;
                  lastName?: string;
                  email?: string;
                  idCard?: string;
                  photoURL?: string;
                }>
              ).map(item => {
                const user = 'user' in item && item.user ? item.user : item;
                const id = String(user.id || '');
                return {
                  id,
                  displayName:
                    resolveUserDisplayName({
                      firstName: user.firstName,
                      lastName: user.lastName,
                      displayName: user.displayName,
                    }) || String(user.email || id),
                  idCard: user.idCard ?? null,
                  photoURL: user.photoURL ?? null,
                  email: user.email ?? null,
                };
              }),
            ),
          );
          setSupervisorsDrawerOpen(false);
        }}
        selectedUsers={plan.form.supervisors.map(supervisor => ({
          id: supervisor.id,
          displayName: supervisor.displayName,
          email: supervisor.email ?? undefined,
          idCard: supervisor.idCard ?? undefined,
          photoURL: supervisor.photoURL ?? undefined,
        }))}
        type={JOURNEY_ASSIGNED_USERS_TYPE.SUPERVISOR}
        title={intl.formatMessage({ id: 'workJourney.management.plan.section.supervisors' })}
        allowUsersWithExistingJourney
      />

      <PlanMonthlyJourneyAddUsersDrawer
        open={plan.addUsersOpen}
        organizationId={plan.organizationId}
        excludeUserIds={plan.form.members.map(member => member.id)}
        onClose={() => plan.setAddUsersOpen(false)}
        onAdd={plan.requestAddCandidate}
      />

      <PlanMonthlyJourneyUnderMinModal
        open={!!plan.underMinImpact}
        impact={plan.underMinImpact}
        onConfirm={plan.confirmUnderMin}
        onCancel={plan.cancelUnderMin}
      />

      <PlanMonthlyJourneySuccessModal
        open={plan.successOpen}
        hadMovedMembers={plan.hadMovedMembers}
        onBackHome={plan.handleSuccessBackHome}
        onPlanAnother={plan.handleSuccessPlanAnother}
      />
    </>
  );
};
